// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// WB-011 — the standalone "sketch an idea" whiteboard surface (Mode B): a blank canvas
// any in-call participant can open, backed by the same WhiteboardEngine + call transport
// as annotation, but full-surface (not anchored to a video) and scoped to the ROOM board
// (board_id = roomID). Minimizable like the call screen, it coexists with the call UI.
//
// Live ops flow over the active call transport (data channel for 1:1/mesh, m.whiteboard
// for SFU); a late opener / reconnecter is brought up to date by LateJoinSync (WB-006).
// The full vector toolbar (shapes/text/sticky) is WB-012; this ships a minimal pen +
// color + undo/clear shell.

import { useEffect, useReducer, useRef, useState } from "react";
import { Button } from "antd";
import { CloseOutlined, DeleteOutlined, DownOutlined, LockOutlined, RedoOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons";
import { WhiteboardEngine, type Tool } from "../whiteboard/engine";
import { LateJoinSync } from "../whiteboard/latejoin";
import { ANNOTATION_PALETTE } from "../whiteboard/colors";
import { isDrawOp, type DrawOp, type Point, type WireOp } from "../whiteboard/types";
import { Power } from "../types";

let opSeq = 0;

// The toolbar palette of tools (glyphs keep it icon-dependency-free).
const TOOLS: { tool: Tool; glyph: string; title: string }[] = [
  { tool: "pen", glyph: "✏", title: "Pen" },
  { tool: "highlighter", glyph: "▬", title: "Highlighter" },
  { tool: "eraser", glyph: "⌫", title: "Eraser" },
  { tool: "line", glyph: "╱", title: "Line" },
  { tool: "rect", glyph: "▭", title: "Rectangle" },
  { tool: "ellipse", glyph: "◯", title: "Ellipse" },
  { tool: "arrow", glyph: "➜", title: "Arrow" },
  { tool: "text", glyph: "T", title: "Text" },
  { tool: "sticky", glyph: "▢", title: "Sticky note" },
];
const WIDTHS: { w: number; title: string }[] = [
  { w: 2, title: "Thin" },
  { w: 4, title: "Medium" },
  { w: 8, title: "Thick" },
];

export function WhiteboardView({
  open,
  minimized,
  author,
  boardId,
  color,
  channelName,
  sendOp,
  register,
  peers,
  initialOps,
  onMinimize,
  onRestore,
  onClose,
  onSave,
  canModerate,
  myPower,
  restrictMinPower,
  onToggleRestrict,
}: {
  readonly open: boolean;
  readonly minimized: boolean;
  readonly author: string;
  readonly boardId: string; // = roomID for the room board
  readonly color: string; // my per-author default color
  readonly channelName: string;
  readonly sendOp: (op: WireOp) => void;
  readonly register: (handler: (op: WireOp) => void) => () => void;
  readonly peers: () => string[]; // connected participants, for late-join responder election
  readonly initialOps?: DrawOp[]; // hydrate from a saved snapshot (WB-014 reopen)
  readonly onMinimize: () => void;
  readonly onRestore: () => void;
  readonly onClose: () => void;
  // Save the current board to the channel as an E2E-sealed snapshot (WB-013).
  readonly onSave?: (ops: DrawOp[]) => void;
  // WB-015 moderation. `canModerate` (operator) shows clear-board + restrict-drawing.
  // `myPower`/`restrictMinPower` make the board read-only when I'm below the threshold.
  readonly canModerate?: boolean;
  readonly myPower?: number;
  readonly restrictMinPower?: number;
  readonly onToggleRestrict?: (minPower: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<WhiteboardEngine | null>(null);
  const [pen, setPen] = useState(color);
  const [tool, setTool] = useState<Tool>("pen");
  const [width, setWidth] = useState(4);
  // An in-progress inline text/sticky entry, anchored at a normalized board point.
  const [textEntry, setTextEntry] = useState<{ at: Point; kind: "text" | "sticky"; value: string } | null>(null);
  const [, bump] = useReducer((x: number) => x + 1, 0); // re-render the toolbar undo/redo affordances
  // Latest values for the once-created engine/late-join closures.
  const sendRef = useRef(sendOp);
  sendRef.current = sendOp;
  const boardRef = useRef(boardId);
  boardRef.current = boardId;
  const peersRef = useRef(peers);
  peersRef.current = peers;
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  // Focus the inline text/sticky editor reliably when it opens (autofocus is unreliable
  // for an element that mounts in response to a pointer event).
  useEffect(() => {
    if (!textEntry) return;
    const id = requestAnimationFrame(() => textRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [textEntry?.at.x, textEntry?.at.y, textEntry?.kind]);

  // Mount the engine + transport wiring once the board is open and the canvas exists.
  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const layout = () => {
      const dpr = globalThis.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(wrap.clientWidth * dpr));
      const h = Math.max(1, Math.round(wrap.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.style.width = `${wrap.clientWidth}px`;
        canvas.style.height = `${wrap.clientHeight}px`;
        canvas.width = w; // clears the canvas…
        canvas.height = h;
        engineRef.current?.refresh(); // …so repaint the op-log
      }
    };

    const engine = new WhiteboardEngine(canvas, {
      author,
      color: pen,
      strokeWidth: width,
      referenceSize: { width: 1600, height: 900 },
      onLocalOp: (op) => {
        sendRef.current({ ...op, board_id: boardRef.current });
        bump();
      },
      // The text/sticky tools open an inline editor at the clicked point.
      onTextRequest: (at, kind) => setTextEntry({ at, kind, value: "" }),
    });
    engine.setTool(tool);
    // Reopening a saved snapshot (WB-014): seed the board with its op-set (no re-emit).
    if (initialOps) for (const o of initialOps) engine.applyRemote(o);
    engineRef.current = engine;

    const lateJoin = new LateJoinSync({
      myUserID: author,
      send: (op) => sendRef.current(op),
      snapshotOps: () => engine.ops,
      applySnapshot: (ops) => {
        for (const o of ops) engine.applyRemote(o);
        bump();
      },
      peers: () => peersRef.current(),
      newId: () => `${author}-snap-${opSeq++}`,
      now: () => Date.now(),
      boardId: boardRef.current,
    });

    const unregister = register((op) => {
      if (op.board_id && op.board_id !== boardRef.current) return;
      if (op.op === "snapshot_request" || op.op === "snapshot") {
        lateJoin.onControl(op, op.author);
        bump();
        return;
      }
      if (isDrawOp(op) || op.op === "erase" || op.op === "clear") {
        engine.applyRemote(op);
        bump();
      }
    });

    layout();
    const ro = new ResizeObserver(layout);
    ro.observe(wrap);
    globalThis.addEventListener("resize", layout);
    // Ask whoever's already on the board for current state — only for a LIVE board with
    // peers. A standalone reopened snapshot (no peers) is already seeded from initialOps.
    if (peersRef.current().length > 0) lateJoin.request();

    return () => {
      unregister();
      ro.disconnect();
      globalThis.removeEventListener("resize", layout);
      engine.destroy();
      engineRef.current = null;
    };
    // Engine identity is stable for the board's lifetime (open→close); author is fixed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    engineRef.current?.setColor(pen);
  }, [pen]);
  useEffect(() => {
    engineRef.current?.setTool(tool);
  }, [tool]);
  useEffect(() => {
    engineRef.current?.setStrokeWidth(width);
  }, [width]);

  const undoMine = () => {
    engineRef.current?.undoLastMine();
    bump();
  };
  const redoMine = () => {
    engineRef.current?.redoLastMine();
    bump();
  };
  const clearMine = () => {
    const e = engineRef.current;
    if (!e) return;
    for (const o of e.ops) if (o.author === author) e.undo(o.id);
    bump();
  };
  // Commit the inline text/sticky entry to the engine (ignored if empty), then close it.
  const commitText = () => {
    const entry = textEntry;
    setTextEntry(null);
    if (entry && entry.value.trim()) engineRef.current?.addText(entry.at, entry.value, entry.kind);
    bump();
  };

  if (!open) return null;

  if (minimized) {
    return (
      <button className="wb-mini" onClick={onRestore} title="Return to the whiteboard">
        ✏️ <span>Whiteboard · {channelName}</span>
      </button>
    );
  }

  // WB-015: read-only when an operator has restricted drawing above my power level.
  const restricted = (myPower ?? 100) < (restrictMinPower ?? 0);

  return (
    <div className="wb-view">
      <div className="wb-view-head">
        <Button shape="circle" type="text" icon={<DownOutlined />} onClick={onMinimize} title="Minimise (back to chat)" />
        <span className="wb-view-title">Whiteboard · {channelName}</span>
        {canModerate && (
          <>
            <Button type={restrictMinPower ? "primary" : "text"} icon={<LockOutlined />} onClick={() => onToggleRestrict?.(restrictMinPower ? 0 : Power.Voice)} title={restrictMinPower ? "Allow everyone to draw" : "Restrict drawing to Voice+ (operators only)"}>
              {restrictMinPower ? "Restricted" : "Restrict"}
            </Button>
            <Button type="text" icon={<DeleteOutlined />} onClick={() => engineRef.current?.clear()} title="Clear the whole board (operators only)">
              Clear board
            </Button>
          </>
        )}
        {onSave && (
          <Button type="text" icon={<SaveOutlined />} onClick={() => onSave(engineRef.current?.ops ?? [])} title="Save this whiteboard to the channel">
            Save
          </Button>
        )}
        <Button shape="circle" type="text" icon={<CloseOutlined />} onClick={onClose} title="Close whiteboard" />
      </div>
      {restricted && <div className="wb-restricted-banner">🔒 An operator restricted drawing — you can view but not draw.</div>}
      <div ref={wrapRef} className="wb-view-canvas-wrap">
        <canvas ref={canvasRef} className="wb-view-canvas" style={{ touchAction: "none", pointerEvents: restricted ? "none" : "auto" }} />
        {textEntry && (
          // Inline editor for text / sticky. Explicit Add / Cancel (Enter / Esc) — no
          // commit-on-blur, so a stray focus change can't dismiss it. stopPropagation keeps
          // clicks inside it off the drawing canvas.
          <div
            className="wb-text-editor"
            style={{ left: `${textEntry.at.x * 100}%`, top: `${textEntry.at.y * 100}%` }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <textarea
              ref={textRef}
              className="wb-text-entry"
              style={{ color: textEntry.kind === "sticky" ? "#11202a" : pen, background: textEntry.kind === "sticky" ? pen : "#0b141a" }}
              value={textEntry.value}
              placeholder={textEntry.kind === "sticky" ? "Sticky note…" : "Text…"}
              onChange={(e) => setTextEntry((cur) => (cur ? { ...cur, value: e.target.value } : cur))}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setTextEntry(null);
                } else if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  commitText();
                }
              }}
            />
            <div className="wb-text-editor-actions">
              <Button size="small" type="primary" onMouseDown={(e) => e.preventDefault()} onClick={commitText}>
                Add
              </Button>
              <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => setTextEntry(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="wb-view-toolbar">
        <div className="wb-tool-group">
          {TOOLS.map((t) => (
            <button
              key={t.tool}
              type="button"
              className={`wb-tool${tool === t.tool ? " wb-tool-active" : ""}`}
              onClick={() => setTool(t.tool)}
              title={t.title}
              aria-label={t.title}
            >
              {t.glyph}
            </button>
          ))}
        </div>
        <div className="wb-tool-group">
          {WIDTHS.map((x) => (
            <button
              key={x.w}
              type="button"
              className={`wb-tool${width === x.w ? " wb-tool-active" : ""}`}
              onClick={() => setWidth(x.w)}
              title={`${x.title} stroke`}
              aria-label={`${x.title} stroke`}
            >
              <span className="wb-width-dot" style={{ width: x.w + 2, height: x.w + 2 }} />
            </button>
          ))}
        </div>
        <div className="wb-tool-group">
          {ANNOTATION_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              className={`wb-swatch${c === pen ? " wb-swatch-active" : ""}`}
              style={{ background: c }}
              onClick={() => setPen(c)}
              title="Color"
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
        <div className="wb-tool-group">
          <Button shape="circle" type="text" icon={<UndoOutlined />} onClick={undoMine} title="Undo my last mark" />
          <Button shape="circle" type="text" icon={<RedoOutlined />} onClick={redoMine} disabled={!engineRef.current?.canRedo} title="Redo" />
          <button type="button" className="wb-tool" onClick={clearMine} title="Clear my marks">
            ⌫
          </button>
        </div>
      </div>
    </div>
  );
}
